import React, { useState, useEffect } from 'react';
import { CertEndingBadge, useCertEndingSoon } from '../components/CertEndingBadge';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import InsuranceFormModal from '../components/InsuranceFormModal';
import AddPatientModal from '../components/modals/AddPatientModal';
import FilterModal from '../components/modals/FilterModal';
import { User, Filter, Plus } from 'lucide-react';
import api from '../api';
import { toast } from 'sonner';

// Exact Alora Plus icon components - 5 icons only
const EditIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#17a2b8]" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const PrintIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 text-gray-600" fill="currentColor">
    <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
  </svg>
);

const WordIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
    <rect x="2" y="2" width="20" height="20" rx="2" fill="#2b5797"/>
    <text x="12" y="16" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold" fontFamily="Arial">W</text>
  </svg>
);

const DocIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#17a2b8]" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const MedicalIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 text-gray-600" fill="currentColor">
    <path d="M20 6h-4V4c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zM10 4h4v2h-4V4z"/>
  </svg>
);

const AdmissionInsurancePage = () => {
  const certEnding = useCertEndingSoon(5);
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEntries, setShowEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [includeInactivated, setIncludeInactivated] = useState(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [sortField, setSortField] = useState('lastName');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({});

  useEffect(() => {
    fetchPatients();
  }, []);

  const fetchPatients = async () => {
    setLoading(true);
    try {
      const response = await api.get('/patients');
      if (response.data.success) {
        setPatients(response.data.patients);
      }
    } catch (error) {
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handlePatientSelect = (patient) => {
    // Navigate to patient detail page
    navigate(`/dashboard/patients/admission/${patient.id}`);
  };

  const handleInsuranceClick = (patient) => {
    setSelectedPatient(patient);
    setShowInsuranceModal(true);
  };

  // Filter and sort patients
  const filteredPatients = patients
    .filter(p => {
      const matchesSearch = 
        `${p.lastName}, ${p.firstName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.firstName?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = includeInactivated ? true : p.status === 'Active';
      
      // Apply advanced filters
      const matchesInsurance = !advancedFilters.insurance || 
        (p.insurance || p.insurance_provider || '')?.toLowerCase().includes(advancedFilters.insurance.toLowerCase());
      const matchesFilterStatus = !advancedFilters.status || p.status === advancedFilters.status;
      const matchesOffice = !advancedFilters.office || 
        (p.officeName || '').toLowerCase().includes(advancedFilters.office.toLowerCase());
      
      // Date filtering
      let matchesDateFrom = true;
      let matchesDateTo = true;
      if (advancedFilters.dateFrom && p.admitDate) {
        const admitDate = new Date(p.admitDate);
        const fromDate = new Date(advancedFilters.dateFrom);
        matchesDateFrom = admitDate >= fromDate;
      }
      if (advancedFilters.dateTo && p.admitDate) {
        const admitDate = new Date(p.admitDate);
        const toDate = new Date(advancedFilters.dateTo);
        matchesDateTo = admitDate <= toDate;
      }
      
      return matchesSearch && matchesStatus && matchesInsurance && matchesFilterStatus && matchesOffice && matchesDateFrom && matchesDateTo;
    })
    .sort((a, b) => {
      let aVal = '', bVal = '';
      if (sortField === 'patientName') {
        aVal = `${a.lastName}, ${a.firstName}`;
        bVal = `${b.lastName}, ${b.firstName}`;
      } else {
        aVal = a[sortField] || '';
        bVal = b[sortField] || '';
      }
      const comparison = aVal.toString().localeCompare(bVal.toString());
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  // Pagination
  const totalPages = Math.ceil(filteredPatients.length / showEntries);
  const startIndex = (currentPage - 1) * showEntries;
  const paginatedPatients = filteredPatients.slice(startIndex, startIndex + showEntries);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    if (dateStr.includes('/')) return dateStr;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  // Generate page numbers exactly like Alora Plus
  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= Math.min(totalPages, 5); i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <DashboardLayout>
      <div className="p-0">
        {/* Page Title - Exact Alora Plus Style */}
        <h1 className="text-2xl font-light text-[#5bc0de] mb-6">
          Admission, Insurance & Prior Auth <span className="text-gray-400 text-lg">&gt; List</span>
        </h1>

        {/* Section Title with Add Button - Exact Clone */}
        <div className="flex items-center gap-3 mb-4">
          <User className="w-5 h-5 text-gray-400" />
          <span className="text-lg text-gray-600 font-light">Admission</span>
          <button 
            className="bg-[#5bc0de] hover:bg-[#46b8da] text-white px-3 py-1.5 rounded text-sm flex items-center gap-1"
            onClick={() => setShowAddPatientModal(true)}
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>

        {/* Search Bar and Show Entries - Exact Clone */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-gray-600 text-sm">Search:</span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="border border-gray-300 rounded px-3 py-1.5 w-48 text-sm focus:outline-none focus:border-[#5bc0de]"
            />
            <button 
              className="bg-[#5bc0de] hover:bg-[#46b8da] text-white p-2 rounded"
              onClick={() => setShowFilterModal(true)}
              data-testid="filter-btn"
            >
              <Filter className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600">Show</span>
            <select
              value={showEntries}
              onChange={(e) => { setShowEntries(Number(e.target.value)); setCurrentPage(1); }}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-gray-600">entries</span>
          </div>
        </div>

        {/* Data Table - Exact Alora Plus Clone */}
        <div className="bg-white border border-gray-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Select
                </th>
                <th 
                  className="px-3 py-3 text-left text-xs font-medium text-[#d9534f] uppercase tracking-wider cursor-pointer hover:text-[#c9302c]"
                  onClick={() => handleSort('patientName')}
                >
                  Patient Name {sortField === 'patientName' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('pan')}>
                  PAN # {sortField === 'pan' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('admitDate')}>
                  Admit Date {sortField === 'admitDate' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('dischargeDate')}>
                  Discharge Date {sortField === 'dischargeDate' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('dateOfBirth')}>
                  DOB {sortField === 'dateOfBirth' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('officeName')}>
                  Office Name {sortField === 'officeName' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
              ) : paginatedPatients.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">No admissions found</td></tr>
              ) : (
                paginatedPatients.map((patient, index) => (
                  <tr key={patient.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-0.5">
                        <button onClick={() => handlePatientSelect(patient)} className="p-0.5 hover:bg-gray-100 rounded" title="Edit">
                          <EditIcon />
                        </button>
                        <button className="p-0.5 hover:bg-gray-100 rounded" title="Print">
                          <PrintIcon />
                        </button>
                        <button className="p-0.5 hover:bg-gray-100 rounded" title="Word Document">
                          <WordIcon />
                        </button>
                        <button className="p-0.5 hover:bg-gray-100 rounded" title="Documents">
                          <DocIcon />
                        </button>
                        <button onClick={() => handlePatientSelect(patient)} className="p-0.5 hover:bg-gray-100 rounded" title="Patient Info">
                          <MedicalIcon />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-900"><div className="flex items-center gap-2 flex-wrap">{patient.lastName}, {patient.firstName} {patient.middleName || ''}<CertEndingBadge info={certEnding[patient.id]} patientId={patient.id} /></div></td>
                    <td className="px-3 py-2 text-gray-700">{patient.pan || ''}</td>
                    <td className="px-3 py-2 text-gray-700">{formatDate(patient.admitDate)}</td>
                    <td className="px-3 py-2 text-gray-700">{formatDate(patient.dischargeDate)}</td>
                    <td className="px-3 py-2 text-gray-700">{formatDate(patient.dateOfBirth)}</td>
                    <td className="px-3 py-2 text-gray-700">{patient.officeName || 'Ivory Home Health Care, Inc.'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination - Exact Alora Plus Clone */}
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-600">
            Showing {filteredPatients.length > 0 ? startIndex + 1 : 0} to {Math.min(startIndex + showEntries, filteredPatients.length)} of {filteredPatients.length} entries
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {getPageNumbers().map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1.5 border rounded text-sm ${
                  currentPage === page 
                    ? 'bg-[#5bc0de] text-white border-[#5bc0de]' 
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Last
            </button>
          </div>
        </div>

        {/* Include Inactivated Checkbox - Exact Clone */}
        <div className="flex items-center gap-2 mt-6">
          <input
            type="checkbox"
            id="includeInactivated"
            checked={includeInactivated}
            onChange={(e) => setIncludeInactivated(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300"
          />
          <label htmlFor="includeInactivated" className="text-sm text-gray-600">
            Include Inactivated Admissions
          </label>
        </div>
      </div>

      {/* Insurance Form Modal */}
      {showInsuranceModal && selectedPatient && (
        <InsuranceFormModal
          patient={selectedPatient}
          onClose={() => setShowInsuranceModal(false)}
          onSave={(updatedPatient) => {
            setPatients(patients.map(p => p.id === updatedPatient.id ? updatedPatient : p));
          }}
        />
      )}

      {/* Add Patient Modal */}
      {showAddPatientModal && (
        <AddPatientModal
          onClose={() => setShowAddPatientModal(false)}
          onSave={() => fetchPatients()}
        />
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <FilterModal
          onClose={() => setShowFilterModal(false)}
          currentFilters={advancedFilters}
          onApply={(newFilters) => {
            setAdvancedFilters(newFilters);
            setCurrentPage(1);
          }}
        />
      )}
    </DashboardLayout>
  );
};

export default AdmissionInsurancePage;
